import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrashAlt,
  faEdit,
  faCheckCircle,
  faTimesCircle,
  faCalendarDay,
  faCalendarCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";

const TYPE_COLORS = {
  1: "#EF9F27", 2: "#F09595", 3: "#5DCAA5", 4: "#888780", 5: "#85B7EB",
  6: "#ED93B1", 7: "#AFA9EC", 8: "#378ADD", 9: "#97C459", 10: "#FAC775",
  11: "#5DCAA5", 12: "#F5C4B3", 13: "#E24B4A", 14: "#9FE1CB", 15: "#D85A30",
  17: "#D3D1C7", 18: "#B4B2A9", 19: "#FAC775", 20: "#7F77DD", 21: "#ED93B1",
};

const getTypeColor = (id) => TYPE_COLORS[id] || "#888780";

const MONTHS_AR = [
  { id: 1, name: "1 - كانون ثاني" },
  { id: 2, name: "2 - شباط" },
  { id: 3, name: "3 - آذار" },
  { id: 4, name: "4 - نيسان" },
  { id: 5, name: "5 - أيار" },
  { id: 6, name: "6 - حزيران" },
  { id: 7, name: "7 - تموز" },
  { id: 8, name: "8 - آب" },
  { id: 9, name: "9 - أيلول" },
  { id: 10, name: "10 - تشرين أول" },
  { id: 11, name: "11 - تشرين ثاني" },
  { id: 12, name: "12 - كانون أول" },
];

const YEARS = [2024, 2025, 2026];
const DAYS_AR = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

const PALESTINIAN_MONTHS = [
  "كانون الثاني",
  "شباط",
  "آذار",
  "نيسان",
  "أيار",
  "حزيران",
  "تموز",
  "آب",
  "أيلول",
  "تشرين الأول",
  "تشرين الثاني",
  "كانون الأول"
];

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = PALESTINIAN_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};



const TasksHolidays = () => {
  const dispatch = useDispatch();
  const tasks = useSelector((state) => state.main.data?.tasksDaysData || []);
  const annualEvents = useSelector((state) => state.main.data?.annualEventsData || []);

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [holidayNames, setHolidayNames] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [editTask, setEditTask] = useState({
    id: null,
    TxtType: "",
    day_date: "",
    day_end: "",
  });

  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);
  const [viewYear] = useState(new Date().getFullYear());
  const [, ] = useState(null); // selectedFilterMonth (unused)
  const [monthAssign, setMonthAssign] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [pickerPos, setPickerPos] = useState(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (tasks.length === 0) {
      dispatch(fetchData()).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, tasks.length]);

  useEffect(() => {
    const loadNames = async () => {
      try {
        const credentials = JSON.parse(localStorage.getItem("userCredentials"));
        const token = credentials?.token;
        const res = await axios.get(
          `https://api.alfares.cloud/api/alfares/holiday_name/${token}`
        );
        if (res.data?.data) setHolidayNames(res.data.data);
      } catch (e) {
        // ignore
      }
    };
    loadNames();
  }, []);

  useEffect(() => {
    if (showEditModal || showMonthlyModal || showEventsModal) {
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [showEditModal, showMonthlyModal, showEventsModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setPickerPos(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTasks = (tasks || [])
    .filter((t) => {
      const matchesSearch = (t.task_name || String(t.TxtType) || "")
        .toString()
        .toLowerCase()
        .includes(filter);
      
      if (!t.day_date) return false;
      const [yearStr, monthStr] = t.day_date.split('T')[0].split('-');
      const taskYear = parseInt(yearStr, 10);
      const taskMonth = parseInt(monthStr, 10);
      
      return matchesSearch && taskMonth === viewMonth && taskYear === viewYear;
    })
    .sort((a, b) => new Date(a.day_date) - new Date(b.day_date));

  // Create a set of task dates for quick lookup
  const taskDatesSet = new Set(
    (tasks || []).map(task => task.day_date?.split('T')[0])
  );

  const filteredEvents = (annualEvents || []).filter(event => {
    if (!event.Fulldays) return false;
    const [yearStr, monthStr] = event.Fulldays.split('T')[0].split('-');
    const eventYear = parseInt(yearStr, 10);
    const eventMonth = parseInt(monthStr, 10);
    return eventMonth === viewMonth && eventYear === viewYear;
  }).sort((a, b) => new Date(a.Fulldays) - new Date(b.Fulldays));

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("يرجى اختيار مهام أولاً");
      return;
    }
    toast(
      (t) => (
        <div className="confirm-toast">
          <p>هل أنت متأكد من حذف {selectedIds.length} مهمة؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const l = toast.loading("جارٍ الحذف...");
                try {
                  const credentials = JSON.parse(
                    localStorage.getItem("userCredentials")
                  );
                  const token = credentials?.token;
                  for (let id of selectedIds) {
                    await axios.delete(
                      `https://api.alfares.cloud/api/alfares/tasksholidays/${id}/${token}`
                    );
                  }
                  dispatch(fetchData());
                  setSelectedIds([]);
                  toast.success("تم الحذف بنجاح");
                } catch {
                  toast.error("فشل الحذف");
                } finally {
                  toast.dismiss(l);
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
            >
              نعم، احذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const handleUpdate = async () => {
    const credentials = JSON.parse(localStorage.getItem("userCredentials"));
    const token = credentials?.token;
    if (!editTask.TxtType || !editTask.day_date) {
      toast.error("يرجى إدخال نوع وتاريخ المهمة");
      return;
    }
    const loadingToast = toast.loading("جارٍ تحديث المهمة...");
    try {
      await axios.put(
        `https://api.alfares.cloud/api/alfares/tasksholidays/${editTask.id}/${token}`,
        {
          TxtType: editTask.TxtType,
          day_date: editTask.day_date,
          day_end: editTask.day_end,
        }
      );
      setEditTask({ id: null, TxtType: "", day_date: "", day_end: "" });
      setShowEditModal(false);
      toast.success("تم تحديث المهمة بنجاح", { id: loadingToast });
      dispatch(fetchData());
    } catch {
      toast.error("فشل في التحديث", { id: loadingToast });
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowEventsModal(false);
    setEditTask({ id: null, TxtType: "", day_date: "", day_end: "" });
  };

  return (
    <div className="transferred-wrapper anim-fade-in" style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box task-icon">
              <FontAwesomeIcon icon={faCalendarDay} />
            </div>
            <div className="header-text-box">
              <h2>أيام المهمات</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة أيام المهمات</span>
              </div>
            </div>
          </div>

          <div className="header-buttons-group">
            <div className="global-search-container">
              <FontAwesomeIcon icon={faSearch} className="s-icon" />
              <input
                type="text"
                placeholder="ابحث عن مهمة..."
                value={filter}
                onChange={(e) => setFilter(e.target.value.toLowerCase())}
              />
            </div>

            <div className="results-count">
              <b>{filteredTasks.length}</b> مهمة
            </div>

            <Can module="holidays" action="delete">
              <button
                className={`bulk-delete-btn ${
                  selectedIds.length > 0 ? "show" : ""
                }`}
                onClick={handleDeleteSelected}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span>حذف المحدد ({selectedIds.length})</span>
              </button>
            </Can>

            <button
              onClick={() => setShowEventsModal(true)}
              className="premium-action-btn"
              style={{ 
                background: filteredEvents.length > 0 ? '#22c55e' : 'var(--panel-2)',
                cursor: filteredEvents.length > 0 ? 'pointer' : 'not-allowed',
                opacity: filteredEvents.length > 0 ? 1 : 0.5
              }}
              disabled={filteredEvents.length === 0}
            >
              <FontAwesomeIcon icon={faCalendarDay} />
              <span>مناسبات الشهر</span>
            </button>
            <button
              onClick={() => {
                const existingAssign = {};
                tasks.forEach(t => {
                  if (t.day_date) {
                    const dateStr = t.day_date.split('T')[0];
                    existingAssign[dateStr] = Number(t.TxtType);
                  }
                });
                setMonthAssign(existingAssign);
                setShowMonthlyModal(true);
              }}
              className="premium-action-btn"
              style={{ background: "#8b5cf6" }}
            >
              <FontAwesomeIcon icon={faCalendarCheck} />
              <span>تعبئة شهرية</span>
            </button>


          </div>
        </div>

        <nav className="month-filter-bar">
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--muted)', alignSelf: 'center', paddingLeft: 12 }}>{filteredTasks.length} مهمة</span>
          {MONTHS_AR.map(m => (
            <button 
              key={m.id}
              className={`nav-square-btn ${viewMonth === m.id ? 'active' : ''}`}
              onClick={() => setViewMonth(m.id)}
            >
              {m.name.split(' - ')[1]}
            </button>
          ))}
        </nav>

        <div className="premium-table-container" style={{ padding: '15px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ جلب السجلات...</span>
            </div>
          ) : (
            <div className="calendar-view-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 15, height: '100%' }}>
              <div className="calendar-grid-container" style={{ direction: 'rtl', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="calendar-header-row" style={{ marginBottom: 8 }}>
                  {DAYS_AR.map(d => <div key={d} className="calendar-day-head" style={{ fontSize: 14 }}>{d}</div>)}
                </div>
                <div className="calendar-days-grid" style={{ flex: 1 }}>
                  {(() => {
                    const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
                    const startDay = new Date(viewYear, viewMonth - 1, 1).getDay();
                    const cells = [];
                    
                    for (let i = 0; i < startDay; i++) {
                      cells.push(<div key={`empty-${i}`} className="calendar-cell empty"></div>);
                    }
                    
                    for (let d = 1; d <= daysInMonth; d++) {
                      const dateStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const dayTasks = tasks.filter(t => t.day_date && t.day_date.split('T')[0] === dateStr);
                      const dayTask = dayTasks[0];
                      const dayOfWeek = (startDay + d - 1) % 7;
                      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6;
                      
                      // Find annual event for this date
                      const event = annualEvents.find(e => {
                        if (!e.Fulldays) return false;
                        const eventDate = new Date(e.Fulldays);
                        return eventDate.getDate() === d && eventDate.getMonth() + 1 === viewMonth;
                      });

                      cells.push(
                        <div 
                          key={d} 
                          className={`calendar-cell ${isWeekend ? 'weekend' : ''}`}
                          style={{ height: 70 }}
                        >
                          {dayTask ? (
                            <div 
                              key={dayTask.id} 
                              className="assigned-type-label" 
                              style={{ 
                                backgroundColor: getTypeColor(dayTask.TxtType),
                                fontSize: '11px',
                                padding: '4px 6px',
                                marginTop: '4px'
                              }}
                            >
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>{formatDate(dayTask.day_date)}</div>
                              <div>{dayTask.task_name || holidayNames.find(h => h.id === Number(dayTask.TxtType))?.h_name || "مهمة"}</div>
                            </div>
                          ) : event ? (
                            <div 
                              className="assigned-type-label" 
                              style={{ 
                                backgroundColor: '#08682bff',
                                fontSize: '11px',
                                padding: '4px 6px',
                                marginTop: '4px'
                              }}
                            >
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>عطلة رسمية</div>
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>{formatDate(dateStr)}</div>
                              <div>{event.Mnwasf}</div>
                            </div>
                          ) : (
                            <span className="date-number" style={{ fontSize: 14 }}>{d}</span>
                          )}
                        </div>
                      );
                    }
                    return cells;
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card">
            <div className="modal-header task-modal-header">
              <div className="icon">
                <FontAwesomeIcon icon={faEdit} />
              </div>
              <h3>تعديل المهمة</h3>
            </div>

            <div className="modal-body">
              <div className="input-field">
                <label>نوع المهمة</label>
                <div className="input-wrapper">
                  <select
                    value={editTask.TxtType}
                    onChange={(e) => setEditTask({ ...editTask, TxtType: e.target.value })}
                    style={{
                      width: "100%",
                      height: "48px",
                      background: "var(--panel-2)",
                      border: "1px solid var(--border)",
                      borderRadius: "12px",
                      padding: "0 14px",
                      color: "var(--text)",
                      fontFamily: "Cairo, sans-serif",
                      fontSize: "15px",
                      outline: "none",
                    }}
                  >
                    <option value="">اختر نوع المهمة</option>
                    {holidayNames.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.h_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="input-field">
                <label>تاريخ البداية</label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    value={editTask.day_date}
                    onChange={(e) => setEditTask({ ...editTask, day_date: e.target.value })}
                    style={{
                      fontWeight: 'normal',
                      fontSize: "15px",
                      fontFamily: "Arial",
                    }}
                  />
                </div>
              </div>

              <div className="input-field">
                <label>تاريخ النهاية</label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    value={editTask.day_end}
                    onChange={(e) => setEditTask({ ...editTask, day_end: e.target.value })}
                    style={{
                      fontWeight: 'normal',
                      fontSize: "15px",
                      fontFamily: "Arial",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Can module="holidays" action="edit">
                <button
                  onClick={handleUpdate}
                  className="premium-submit-btn task-submit"
                >
                  <div className="btn-content">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>تحديث البيانات</span>
                  </div>
                  <div className="btn-glow"></div>
                </button>
              </Can>
              <button onClick={closeModals} className="cancel-btn">
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>إلغاء</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showEventsModal && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card" style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'normal' }}>مناسبات الشهر</h3>
              <button onClick={() => setShowEventsModal(false)} className="close-x-btn">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '350px', overflowY: 'auto', padding: '10px 0' }}>
              {filteredEvents.length === 0 ? (
                <div className="empty-ui-state" style={{ padding: '30px 20px' }}>
                  <div className="icon"><FontAwesomeIcon icon={faCalendarDay} /></div>
                  <h3>لا توجد مناسبات لهذا الشهر</h3>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', direction: 'rtl' }}>
                  {filteredEvents.map(event => {
                    const eventDateStr = event.Fulldays?.split('T')[0];
                    const isOfficialHoliday = taskDatesSet.has(eventDateStr);
                    return (
                      <div key={event.Mn_ID || event.Fulldays} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--panel-2)', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '16px', fontWeight: 'normal', color: 'var(--text)' }}>
                            {event.Mnwasf}{isOfficialHoliday ? ' - عطلة رسمية' : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          <span style={{ fontSize: '14px', color: 'var(--muted)' }}>{formatDate(event.Fulldays)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showMonthlyModal && (
        <div className={`premium-modal-overlay full-screen monthly-overlay ${animateIn ? "active" : ""}`} style={{ position: 'absolute', padding: 0, top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', alignItems: 'stretch', justifyContent: 'stretch' }}>
          <div className="premium-modal-card monthly-modal" style={{ maxWidth: '100%', width: '100%', height: '100%', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '15px 20px' }}>
            <div className="modal-header task-modal-header" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="icon" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
              <h3 style={{ margin: 0, flex: 1 }}>التعبئة الشهرية للمهمات</h3>
              <select 
                value={selectedMonth} 
                onChange={(e) => {
                  setSelectedMonth(Number(e.target.value));
                  const existingAssign = {};
                  tasks.forEach(t => {
                    if (t.day_date) {
                      const dateStr = t.day_date.split('T')[0];
                      existingAssign[dateStr] = Number(t.TxtType);
                    }
                  });
                  setMonthAssign(existingAssign);
                }} 
                className="monthly-select" 
                style={{ minWidth: 130 }}
              >
                {MONTHS_AR.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <select 
                value={selectedYear} 
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                  const existingAssign = {};
                  tasks.forEach(t => {
                    if (t.day_date) {
                      const dateStr = t.day_date.split('T')[0];
                      existingAssign[dateStr] = Number(t.TxtType);
                    }
                  });
                  setMonthAssign(existingAssign);
                }} 
                className="monthly-select" 
                style={{ minWidth: 80 }}
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={() => setShowMonthlyModal(false)} className="close-x-btn">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden', minHeight: 0 }}>
              <div className="calendar-grid-container" style={{ direction: 'rtl', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div className="calendar-header-row">
                  {DAYS_AR.map(d => <div key={d} className="calendar-day-head">{d}</div>)}
                </div>
                <div className="calendar-days-grid">
                  {(() => {
                    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                    const startDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
                    const cells = [];
                    for (let i = 0; i < startDay; i++) {
                      cells.push(<div key={`empty-${i}`} className="calendar-cell empty"></div>);
                    }
                    for (let d = 1; d <= daysInMonth; d++) {
                      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const typeId = monthAssign[dateStr];
                      const typeName = holidayNames.find(h => h.id === typeId)?.h_name;
                      const dayOfWeek = (startDay + d - 1) % 7;
                      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Fri, Sat
                      
                      // Find annual event for this date
                      const event = annualEvents.find(e => {
                        if (!e.Fulldays) return false;
                        const eventDate = new Date(e.Fulldays);
                        return eventDate.getDate() === d && eventDate.getMonth() + 1 === selectedMonth;
                      });

                      cells.push(
                        <div 
                          key={d} 
                          className={`calendar-cell ${isWeekend ? 'weekend' : ''}`}
                          style={{ 
                            backgroundColor: typeId ? `${getTypeColor(typeId)}22` : (event ? 'rgba(34, 197, 94, 0.2)' : undefined),
                            border: pickerPos?.dateKey === dateStr ? '2px solid #b8860b' : undefined,
                            borderRadius: pickerPos?.dateKey === dateStr ? '8px' : undefined
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const pickerMaxHeight = 260;
                            const gap = 4;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            
                            let topPosition;
                            if (spaceBelow < pickerMaxHeight + gap) {
                              // Not enough space below, open above with gap
                              topPosition = rect.top + window.scrollY - pickerMaxHeight - gap;
                            } else {
                              // Enough space below, open below with gap
                              topPosition = rect.bottom + window.scrollY + gap;
                            }
                            
                            setPickerPos({ 
                              top: topPosition, 
                              left: rect.left + window.scrollX, 
                              width: rect.width, 
                              dateKey: dateStr 
                            });
                          }}
                        >
                          {typeId ? (
                            <div className="assigned-type-label" style={{ backgroundColor: getTypeColor(typeId), padding: '4px 6px', fontSize: '11px' }}>
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>{formatDate(dateStr)}</div>
                              <div>{typeName}</div>
                            </div>
                          ) : event ? (
                            <div className="assigned-type-label" style={{ backgroundColor: '#22c55e', padding: '4px 6px', fontSize: '11px' }}>
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>عطلة رسمية</div>
                              <div style={{ fontSize: '9px', opacity: 0.9, marginBottom: '1px' }}>{formatDate(dateStr)}</div>
                              <div>{event.Mnwasf}</div>
                            </div>
                          ) : (
                            <span className="date-number">{d}</span>
                          )}
                        </div>
                      );
                    }
                    return cells;
                  })()}
                </div>
              </div>

              <div className="monthly-legend">
                {holidayNames.filter(h => Object.values(monthAssign).includes(h.id)).map(h => (
                  <div key={h.id} className="legend-item">
                    <span className="dot" style={{ backgroundColor: getTypeColor(h.id) }}></span>
                    <span className="label">{h.h_name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer" style={{ marginTop: 10, flexShrink: 0 }}>
              <button 
                className="premium-submit-btn" 
                style={{ background: '#1b61c9', flex: 2, border: 'none', color: 'white', height: 48, borderRadius: 12, fontSize: 15, cursor: 'pointer', fontFamily: 'Cairo, sans-serif', fontWeight: 'normal' }}
                onClick={async () => {
                  const dates = Object.keys(monthAssign);
                  if (dates.length === 0) return toast.error("لا توجد تعيينات لحفظها");
                  const l = toast.loading("جارٍ حفظ المهمات...");
                  try {
                    const credentials = JSON.parse(localStorage.getItem("userCredentials"));
                    const token = credentials?.token;

                    const existingForMonth = tasks.filter(t => {
                      if (!t.day_date) return false;
                      const d = new Date(t.day_date);
                      return d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear;
                    });
                    for (const t of existingForMonth) {
                      await axios.delete(`https://api.alfares.cloud/api/alfares/tasksholidays/${t.id}/${token}`);
                    }
                    for (const date of dates) {
                      await axios.post(`https://api.alfares.cloud/api/alfares/tasksholidays/${token}`, {
                        TxtType: monthAssign[date],
                        day_date: date,
                        day_end: date,
                        delchk: 0
                      });
                    }
                    toast.success("تم حفظ مهمات الشهر بنجاح", { id: l });
                    setShowMonthlyModal(false);
                    setMonthAssign({});
                    await dispatch(fetchData());
                  } catch (e) {
                    toast.error("فشل الحفظ", { id: l });
                  }
                }}
              >
                حفظ الشهر
              </button>
              <button className="cancel-btn" style={{ flex: 1 }} onClick={() => setMonthAssign({})}>
                تفريغ الكل
              </button>
              <button className="cancel-btn" style={{ flex: 1 }} onClick={() => setShowMonthlyModal(false)}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerPos && createPortal(
        <div 
          ref={pickerRef}
          style={{ position: 'absolute', top: pickerPos.top, left: pickerPos.left, width: pickerPos.width, zIndex: 99999, background: '#1e293b', border: '2px solid #b8860b', borderRadius: 10, maxHeight: 260, overflowY: 'auto', fontFamily: 'Cairo, sans-serif', direction: 'rtl', boxSizing: 'border-box' }}
        >
          <div className="picker-header">اختر نوع المهمة</div>
          <div className="picker-options">
            <div className="picker-option clear" onClick={() => {
              const newAssign = { ...monthAssign };
              delete newAssign[pickerPos.dateKey];
              setMonthAssign(newAssign);
              setPickerPos(null);
            }}>مسح</div>
            {holidayNames.map(h => (
              <div key={h.id} className="picker-option" onClick={() => {
                setMonthAssign({ ...monthAssign, [pickerPos.dateKey]: h.id });
                setPickerPos(null);
              }}>
                <span className="dot" style={{ backgroundColor: getTypeColor(h.id) }}></span>
                {h.h_name}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .transferred-wrapper { padding: 5px; color: var(--text); font-family: 'Cairo', sans-serif; }
        .premium-edit-container {
          background: transparent;
          border: none;
          border-radius: 0;
          margin: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }

        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          direction: rtl;
          gap: 20px;
        }

        .header-left { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
        }
        
        .header-icon-box {
          width: 32px; height: 32px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
          box-shadow: 0 4px 10px -2px rgba(59, 130, 246, 0.4);
        }
        
        .header-text-box h2 { 
          margin: 0; 
          font-size: 18px; 
          font-weight: normal; 
          color: var(--text); 
          white-space: nowrap;
        }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 10px; margin-top: 1px; }
        .current-page { color: #1d4ed8; font-weight: normal; }

        .header-buttons-group {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: nowrap;
        }
        .header-left-side { width: 100%; }
        .global-search-container { background: var(--panel-2); border: 1px solid var(--border); border-radius: 10px; padding: 0 15px; display: flex; align-items: center; gap: 12px; width: 280px; height: 44px; transition: 0.3s; box-sizing: border-box; }
        .global-search-container:focus-within { border-color: #3b82f6; background: var(--bg); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        .s-icon { color: #3b82f6; font-size: 14px; }
        .global-search-container input { background: transparent; border: none; color: var(--text); font-size: 12px; width: 100%; outline: none; font-family: inherit; }
        .results-count {
          background: var(--panel-2);
          padding: 0 15px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--muted);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 6px;
          height: 44px;
          white-space: nowrap;
          transition: all 0.3s;
        }
        .results-count b { color: #3b82f6; }
        .premium-action-btn { background: #3b82f6; color: white; border: none; border-radius: 8px; padding: 6px 14px; display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; cursor: pointer; transition: 0.3s; box-shadow: 0 4px 10px -2px rgba(59, 130, 246, 0.4); font-family: 'Cairo', sans-serif; white-space: nowrap; }
        .premium-action-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .bulk-delete-btn { background: #ef4444; color: white; border: none; border-radius: 8px; display: none; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; cursor: pointer; font-family: 'Cairo', sans-serif; transition: 0.3s; white-space: nowrap; }
        .bulk-delete-btn.show { display: flex; }
        .premium-table-container { 
          padding: 25px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
        }
        
        /* Modern Grid & Cards */
        .holidays-modern-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px; direction: rtl;
        }

        .holiday-card-modern {
          background: var(--panel-2); border: 1px solid var(--border); border-radius: 16px;
          padding: 15px; display: flex; align-items: center; gap: 15px; position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; overflow: hidden;
        }

        .holiday-card-modern:hover {
          transform: translateY(-3px); border-color: #3b82f6;
          box-shadow: 0 10px 20px -8px rgba(0,0,0,0.4); background: var(--panel);
        }

        .holiday-card-modern.is-today {
          border-color: #3b82f6; background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, transparent 100%);
        }
        .holiday-card-modern.is-today::after {
          content: ''; position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: #3b82f6;
        }

        .holiday-card-modern.is-selected {
          border-color: #3b82f6; background: rgba(59, 130, 246, 0.1);
        }

        .card-checkbox-wrapper { position: absolute; top: 12px; left: 12px; z-index: 5; }
        .card-checkbox-wrapper input { width: 16px; height: 16px; cursor: pointer; }

        .holiday-calendar-icon {
          width: 55px; height: 65px; background: var(--panel); border: 1px solid var(--border);
          border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; flex-shrink: 0;
          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
        }

        .calendar-top { color: white; font-size: 10px; font-weight: normal; text-align: center; padding: 2px 0; text-transform: uppercase; }
        .calendar-body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; background: white; color: #1e293b; }
        .day-num { font-size: 20px; font-weight: normal; line-height: 1; }
        .day-name { font-size: 9px; font-weight: normal; margin-top: 1px; opacity: 0.7; }

        .holiday-content-main { flex: 1; }
        .holiday-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
        .holiday-name { margin: 0; font-size: 15px; font-weight: normal; color: var(--text); }
        .today-badge { color: white; font-size: 9px; font-weight: normal; padding: 1px 6px; border-radius: 4px; }

        .holiday-full-date { display: flex; align-items: center; gap: 6px; color: var(--muted); font-size: 12px; font-weight: normal; }
        .date-icon { font-size: 12px; }

        .holiday-card-actions {
          position: absolute; bottom: 12px; left: 12px; opacity: 0; transform: translateX(-8px); transition: 0.3s;
        }
        .holiday-card-modern:hover .holiday-card-actions { opacity: 1; transform: translateX(0); }

        .quick-action-btn {
          width: 28px; height: 28px; border-radius: 6px; border: none;
          background: var(--panel-2); color: var(--muted); cursor: pointer;
          display: flex; align-items: center; justify-content: center; transition: 0.2s;
          font-size: 12px;
        }
        .quick-action-btn:hover { background: #3b82f6; color: white; }

        .loader-overlay { padding: 40px; text-align: center; color: var(--muted); display: flex; flex-direction: column; gap: 15px; }
        .premium-loader { width: 32px; height: 32px; border: 3px solid rgba(59, 130, 246, 0.1); border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .empty-ui-state { text-align: center; padding: 60px 20px !important; color: var(--muted); }
        .empty-ui-state .icon { font-size: 50px; opacity: 0.2; margin-bottom: 20px; color: #3b82f6; }
        .empty-ui-state h3 { font-size: 18px; font-weight: normal; color: var(--text); margin-bottom: 8px; }
        .empty-ui-state p { font-size: 14px; }
        .premium-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(13, 35, 32, 0.45); backdrop-filter: blur(10px); display: flex; align-items: center; justify-content: center; z-index: 10000; opacity: 0; pointer-events: none; transition: 0.4s; }
        .premium-modal-overlay.monthly-overlay { position: absolute; }
        .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
        .premium-modal-card { background: var(--panel); border: 1px solid var(--border); width: 100%; max-width: 440px; border-radius: 20px; padding: 30px; transform: translateY(30px) scale(0.95); transition: 0.4s; box-shadow: 0 40px 80px -20px rgba(0,0,0,0.6); }
        .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }
        .modal-header { display: flex; align-items: center; gap: 15px; margin-bottom: 25px; }
        .task-modal-header .icon { width: 50px; height: 50px; background: rgba(59, 130, 246, 0.12); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #3b82f6; }
        .modal-header h3 { margin: 0; font-size: 20px; font-weight: normal; }
        .input-field { margin-bottom: 20px; }
        .input-field label { display: block; font-size: 13px; font-weight: normal; color: var(--text); margin-bottom: 10px; }
        .input-wrapper { position: relative; display: flex; align-items: center; }
        .input-wrapper input, .input-wrapper select { width: 100%; height: 48px; background: var(--panel-2); border: 1px solid var(--border); border-radius: 12px; padding: 0 15px; color: var(--text); font-family: 'Cairo', inherit; font-size: 14px; transition: 0.3s; outline: none; }
        .input-wrapper input:focus, .input-wrapper select:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
        .modal-footer { display: flex; gap: 15px; margin-top: 30px; }
        .task-submit { flex: 2; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); border: none; color: white; height: 48px; border-radius: 12px; font-size: 16px; font-weight: normal; cursor: pointer; position: relative; overflow: hidden; transition: 0.3s; font-family: 'Cairo', sans-serif; box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4); }
        .btn-content { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; gap: 10px; }
        .btn-glow { position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%); opacity: 0; transition: 0.3s; }
        .task-submit:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .task-submit:hover .btn-glow { opacity: 1; }
        .cancel-btn { flex: 1; background: var(--panel-2); border: 1px solid var(--border); color: var(--text); border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 15px; font-weight: normal; cursor: pointer; font-family: 'Cairo', sans-serif; transition: 0.2s; }
        .cancel-btn:hover { background: #fee2e2; border-color: #ef4444; color: #ef4444; }
        .confirm-toast { text-align: center; font-family: 'Cairo', sans-serif; }
        .toast-actions { display: flex; justify-content: center; gap: 12px; margin-top: 20px; }
        .btn-confirm { background: #ef4444; color: white; border: none; padding: 10px 22px; border-radius: 12px; cursor: pointer; font-weight: normal; font-family: 'Cairo', sans-serif; }
        @media (max-width: 1000px) {
          .premium-header-modern { flex-direction: column; align-items: stretch; padding: 20px; }
          .header-left { justify-content: center; margin-bottom: 15px; }
          .header-tools, .header-buttons-group { flex-direction: column; width: 100%; gap: 10px; }
          .global-search-container, .results-count { display: none !important; }
          .global-search-container { max-width: 100%; order: 3; }
          .bulk-delete-btn { width: 100%; justify-content: center; margin-bottom: 10px; display: none; position: relative; }
          .bulk-delete-btn.show { display: flex; }
          .premium-action-btn { width: 100%; justify-content: center; }
          .holidays-modern-grid { grid-template-columns: 1fr; }
        }

        /* Monthly Modal Styles */
        .monthly-modal { direction: rtl; }
        .close-x-btn { background: none; border: none; color: var(--muted); cursor: pointer; font-size: 20px; padding: 5px; transition: 0.2s; }
        .close-x-btn:hover { color: #ef4444; }
        .monthly-select { height: 44px; padding: 0 15px; border-radius: 10px; background: var(--panel-2); border: 1px solid var(--border); color: var(--text); font-family: 'Cairo', sans-serif; font-size: 14px; outline: none; min-width: 150px; }

        /* Month Filter Bar - Standard Schools Pattern */
        .month-filter-bar { 
          display: flex; 
          gap: 6px; 
          overflow-x: auto; 
          padding: 12px 20px; 
          background: rgba(255, 255, 255, 0.8); 
          backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--border); 
          scrollbar-width: none; 
          align-items: center;
        }
        .month-filter-bar::-webkit-scrollbar { display: none; }
        
        .nav-square-btn {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text);
          padding: 8px 16px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: inherit;
          font-weight: normal;
          font-size: 13px;
          white-space: nowrap;
        }

        .nav-square-btn:hover {
          background: rgba(14, 165, 233, 0.1);
          color: var(--accent);
          border-color: var(--accent);
          transform: translateY(-1px);
        }

        .nav-square-btn.active {
          background: var(--accent) !important;
          color: white !important;
          border-color: var(--accent) !important;
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
        }

        /* Monthly Grouping */
        .holidays-scroll-area { padding: 20px; display: flex; flexDirection: column; gap: 30px; overflow-y: auto; flex: 1; }
        .month-group { display: flex; flex-direction: column; gap: 12px; }
        .month-group-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; border-bottom: 1px solid var(--border); margin-bottom: 4px; }
        .month-title { font-size: 16px; font-weight: normal; color: #3b82f6; font-family: 'Cairo', sans-serif; }
        .month-count { font-size: 12px; font-weight: normal; color: var(--muted); background: var(--panel-2); padding: 4px 10px; border-radius: 20px; }

        /* Horizontal Cards */
        .holidays-horizontal-list { display: flex; flex-direction: column; gap: 8px; }
        .holiday-horizontal-card { height: 56px; background: var(--panel); border: 1px solid var(--border); border-radius: 12px; display: flex; align-items: center; padding: 0 15px; cursor: pointer; transition: 0.2s; position: relative; gap: 15px; }
        .holiday-horizontal-card:hover { transform: translateX(-4px); background: var(--panel-2); border-color: #3b82f6; }
        .holiday-horizontal-card.is-today { background: rgba(59, 130, 246, 0.05); }
        .holiday-horizontal-card.is-selected { border-color: #3b82f6; background: rgba(59, 130, 246, 0.08); }
        
        .card-info-main { display: flex; align-items: center; gap: 15px; flex: 1; min-width: 0; }
        .day-name { font-size: 14px; font-weight: normal; color: var(--text); min-width: 70px; }
        .full-date { font-size: 13px; font-weight: normal; color: var(--muted); min-width: 90px; }
        .task-type-text { font-size: 14px; font-weight: normal; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .card-side-badge { display: flex; align-items: center; gap: 8px; background: var(--panel-2); padding: 6px 12px; border-radius: 30px; border: 1px solid var(--border); }
        .type-dot { width: 8px; height: 8px; border-radius: 50%; }
        .type-badge-name { font-size: 11px; font-weight: normal; color: var(--text); }
        
        .today-indicator { font-size: 10px; font-weight: normal; color: white; background: #3b82f6; padding: 2px 8px; border-radius: 4px; margin-right: auto; }
        .calendar-header-row { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; margin-bottom: 10px; }
        .calendar-day-head { text-align: center; font-weight: normal; color: var(--muted); font-size: 13px; }
        .calendar-days-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; flex: 1; }
        .calendar-cell { height: auto; min-height: 55px; flex: 1; background: var(--panel-2); border: 1px solid var(--border); border-radius: 12px; position: relative; padding: 6px; cursor: pointer; transition: 0.2s; display: flex; flex-direction: column; gap: 5px; }
        .calendar-cell:hover { border-color: #8b5cf6; background: var(--bg); }
        .calendar-cell.weekend { background: rgba(255, 255, 255, 0.03); }
        .date-number { font-weight: normal; font-size: 16px; color: var(--muted); }
        .assigned-type-label { font-size: 10px; font-weight: normal; color: white; padding: 8px 8px; border-radius: 6px; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        
        .inline-type-picker { position: absolute; top: 100%; left: 0; right: 0; background: var(--panel); border: 1px solid var(--border); border-radius: 12px; z-index: 100; box-shadow: 0 10px 25px rgba(0,0,0,0.5); overflow: hidden; max-height: 250px; display: flex; flex-direction: column; }
        .picker-header { padding: 10px; font-size: 11px; font-weight: normal; color: var(--muted); border-bottom: 1px solid var(--border); background: #ffffff; }
        .picker-options { overflow-y: auto; flex: 1; }
        .picker-option { padding: 10px 15px; font-size: 13px; font-weight: normal; color: var(--text); display: flex; align-items: center; gap: 10px; transition: 0.2s; font-family: 'Cairo', sans-serif; }
        .picker-option:hover { background: rgba(139, 92, 246, 0.15); color: #8b5cf6; }
        .picker-option.clear { color: #ef4444; border-bottom: 1px solid var(--border); }
        .picker-option .dot { width: 8px; height: 8px; border-radius: 50%; }
        
        .monthly-legend { display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; padding-top: 10px; border-top: 1px solid var(--border); }
        .legend-item { display: flex; align-items: center; gap: 6px; }
        .legend-item .dot { width: 10px; height: 10px; border-radius: 50%; }
        .legend-item .label { font-size: 12px; font-weight: normal; color: var(--muted); }
      `}</style>
    </div>
  );
};

export default TasksHolidays;
